package unlock

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextInput
import puzzlekey.Bundle
import puzzlekey.GiftBroken
import puzzlekey.GiftOpened
import puzzlekey.RecoveryStep
import puzzlekey.Unlock

/**
 * The Recovery code input behind "Stuck?" (ADR-0006): he sends her the code in a messenger, she types it
 * here. [unlock] is null until `sealed.json` has loaded.
 */
@Composable
internal fun Recovery(unlock: Unlock?, onOpened: (Bundle) -> Unit, onBroken: () -> Unit, onError: (String) -> Unit, onBack: () -> Unit) {
    var typed by remember { mutableStateOf("") }
    var problem by remember { mutableStateOf<String?>(null) }
    var busy by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    Div({ classes("card") }) {
        H1 { Text("Lost among the stars?") }
        P { Text("Ask him for the recovery code, and write it here.") }
        Form(attrs = {
            classes("recovery")
            onSubmit { event ->
                event.preventDefault()
                val u = unlock ?: return@onSubmit
                if (busy) return@onSubmit
                busy = true
                scope.launch {
                    try {
                        when (val step = u.recover(typed)) {
                            is GiftOpened -> onOpened(step.bundle)
                            GiftBroken -> onBroken()
                            RecoveryStep.NotACode -> problem = "That doesn't look like the code: it has 20 letters and digits, like XXXX-XXXX-XXXX-XXXX-XXXX."
                            RecoveryStep.WrongCode -> problem = "That code doesn't open it. Check each letter and try again."
                        }
                    } catch (e: Throwable) {
                        problem = "Something went wrong. Try again."
                        onError("recovery: ${e.message ?: e}")
                    } finally {
                        busy = false
                    }
                }
            }
        }) {
            TextInput(typed) {
                classes("code")
                placeholder("XXXX-XXXX-XXXX-XXXX-XXXX")
                attr("autocapitalize", "characters")
                attr("autocomplete", "off")
                attr("autocorrect", "off")
                attr("spellcheck", "false")
                attr("enterkeyhint", "go")
                onInput {
                    typed = it.value
                    problem = null
                }
            }
            Button({
                classes("pill")
                type(ButtonType.Submit)
                if (unlock == null || busy || typed.isBlank()) disabled()
            }) { Text("Open") }
        }
        if (unlock == null) P({ classes("detail") }) { Text("The stars are still gathering: a moment…") }
        problem?.let { P({ classes("problem") }) { Text(it) } }
        TextLink("Back to the stars", onBack)
    }
}
